import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box, Typography, Paper, Grid, CircularProgress, Alert, Chip, Divider, Button,
  Stack, Badge, Tabs, Tab, Snackbar
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import ScienceIcon from '@mui/icons-material/Science';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { getKitchenItemsScreen } from '../../../../../services/Admin/OrderProcessingScreensApi';
import { useAuth } from '../../../../../Context/AuthContext';
import ChangeOrderItemsStatue from './ChangeOrderItemsStatue';

const EXCLUDED_KITCHEN_CATEGORY_IDS = [12, 13];
const EXCLUDED_KITCHEN_CATEGORY_NAMES = ['BAKERY', 'DEASERT', 'DESSERT'];

const isExcludedKitchenCategoryItem = (item) => {
  const product = item?.product || {};
  const category = product?.category || item?.category || {};
  const categoryId = item?.category_id ?? product?.category_id ?? category?.id;

  if (EXCLUDED_KITCHEN_CATEGORY_IDS.includes(Number(categoryId))) return true;

  const categoryNames = [
    category?.name,
    category?.name_en,
    category?.code,
    category?.slug,
    product?.category_name,
    product?.categoryName,
    item?.category_name,
    item?.categoryName,
  ];

  return categoryNames.some((name) => (
    EXCLUDED_KITCHEN_CATEGORY_NAMES.includes(String(name || '').trim().toUpperCase())
  ));
};

// Helper: Build payload for updateOrder, always include all fields (null for missing)
// eslint-disable-next-line no-unused-vars
function buildOrderPayload(order, status) {
  // صحح الـ status ليكون مطابق للـ API
  const validStatuses = ['pending','finished','canceled','in_preparation','out_of_preparation','delivered','under_review'];
  const finalStatus = validStatuses.includes(status) ? status : 'in_preparation';

  return {
    status: finalStatus,
    table_id: order.order_type === 'tables' ? order.table_id ?? null : undefined,
    delivery_id: order.order_type === 'delivery' ? order.delivery_id ?? null : undefined,
    captain_order_id: order.captain_order_id ?? null,
    date: order.date ?? new Date().toISOString().split('T')[0], // YYYY-MM-DD
    order_type: order.order_type ?? undefined,
  };
}
const orderTypeLabel = (type) => {
  if (type === 'delivery') return 'توصيل';
  if (type === 'takeaway') return 'تيك أواي';
  if (type === 'tables') return 'طاولة';
  return type || '-';
};


const orderStatusLabel = (status) => {
  const map = {
    pending: 'جديد لم يحضر',
    under_review: 'قيد المراجعة',
    in_prepration: 'في التحضير',
    in_preparation: 'في التحضير',
    out_of_preparation: 'جاهز للاستلام',
    pickup: 'تم الاستلام من العميل',
    finished: 'مكتمل',
    canceled: 'ملغي',
  };
  return map[status] || status || '-';
};

const getStatusChip = (status) => {
  const configs = {
    pending: { color: 'error', label: 'جديد لم يحضر' },
    in_prepration: { color: 'warning', label: 'في التحضير' },
    in_preparation: { color: 'warning', label: 'في التحضير' },
    out_of_preparation: { color: 'success', label: 'جاهز' },
    pickup: { color: 'success', label: 'تم الاستلام من العميل' },
    finished: { color: 'success', label: 'مكتمل' },
    canceled: { color: 'error', label: 'ملغي' },
  };
  const cfg = configs[status] || { color: 'default', label: status || '-' };
  return <Chip size="small" color={cfg.color} label={cfg.label} sx={{ fontWeight: 700 }} />;
};

const getOrderTypeColor = (type) => {
  if (type === 'delivery') return 'primary';
  if (type === 'takeaway') return 'warning';
  if (type === 'tables') return 'success';
  return 'default';
};

const formatTime = (timeValue) => {
  if (!timeValue) return '-';
  const [h, m] = String(timeValue).split(':');
  const hour24 = Number(h);
  const minute = Number(m);
  if (Number.isNaN(hour24) || Number.isNaN(minute)) return timeValue;
  const period = hour24 >= 12 ? 'م' : 'ص';
  const hour12 = ((hour24 + 11) % 12) + 1;
  const minuteText = String(minute).padStart(2, '0');
  return `${hour12}:${minuteText} ${period}`;
};

const toOpenTimeSeconds = (timeValue) => {
  if (!timeValue || typeof timeValue !== 'string') return 0;
  const parts = timeValue.split(':').map((part) => Number(part));
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return 0;
  const [h = 0, m = 0, s = 0] = parts;
  return (h * 3600) + (m * 60) + s;
};

const toDateMs = (value) => {
  if (!value) return 0;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? 0 : ms;
};

const getActionConfigByStatus = (status) => {
  if (status === 'pending') {
    return { label: 'استلام التجهيز', nextStatus: 'in_prepration' };
  }
  if (status === 'in_prepration') {
    return { label: 'إنهاء التجهيز', nextStatus: 'pickup' };
  }
  if (status === 'out_of_preparation') {
    return { label: 'جاهز للاستلام', nextStatus: 'pickup' };
  }
  return { label: 'تم التحديث', nextStatus: status };
};

const getOrderDisplayNumber = (order = {}, fallbackId = '') => (
  order.general_increment ?? order.daily_increment ?? order.id ?? fallbackId
);

const getKitchenItemName = (item = {}) => {
  const drink = item.drinks || {};
  const product = item.product || {};

  if (item.drinks) {
    return drink.drink_name || drink.name || 'مشروب';
  }

  return product.name || item.name || 'صنف';
};

const KitchenScreen = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [apiErrors, setApiErrors] = useState([]);
  const [changeStatusOpen, setChangeStatusOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderData, setSelectedOrderData] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [targetStatus, setTargetStatus] = useState('pickup');
  const [activeTab, setActiveTab] = useState('kitchen');
  const [kitchenAlert, setKitchenAlert] = useState({ open: false, message: '' });
  const previousKitchenSnapshotRef = useRef(null);
  const firstKitchenFetchRef = useRef(true);

  const buildKitchenSnapshot = (ordersData = {}) => (
    Object.entries(ordersData).reduce((snapshot, [orderId, items]) => {
      snapshot[orderId] = {
        order: items?.[0]?.order || {},
        items: (items || []).reduce((itemsMap, item) => ({
          ...itemsMap,
          [item.id]: item,
        }), {}),
      };

      return snapshot;
    }, {})
  );

  const detectKitchenAlerts = (nextData = {}) => {
    const nextSnapshot = buildKitchenSnapshot(nextData);
    const previousSnapshot = previousKitchenSnapshotRef.current;

    if (!previousSnapshot || firstKitchenFetchRef.current) {
      previousKitchenSnapshotRef.current = nextSnapshot;
      firstKitchenFetchRef.current = false;
      return;
    }

    const alerts = [];

    Object.entries(nextSnapshot).forEach(([orderId, orderSnapshot]) => {
      const previousOrderSnapshot = previousSnapshot[orderId];
      const orderNumber = getOrderDisplayNumber(orderSnapshot.order, orderId);

      if (!previousOrderSnapshot) {
        alerts.push(`تم إضافة طلب جديد #${orderNumber}`);
        return;
      }

      Object.entries(orderSnapshot.items).forEach(([itemId, item]) => {
        if (!previousOrderSnapshot.items[itemId]) {
          alerts.push(`تم إضافة صنف جديد: ${getKitchenItemName(item)} في طلب #${orderNumber}`);
        }
      });
    });

    previousKitchenSnapshotRef.current = nextSnapshot;

    if (alerts.length) {
      const extraCount = alerts.length - 2;
      setKitchenAlert({
        open: true,
        message: extraCount > 0
          ? `${alerts.slice(0, 2).join(' - ')} +${extraCount} تنبيه`
          : alerts.join(' - '),
      });
    }
  };

  // تعديل طريقة جلب البيانات لتتوافق مع شكل البيانات الجديد (data: [[order, ...], ...])
  const fetchKitchenData = async () => {
    setLoading(true);
    setError('');
    setApiErrors([]);
    try {
      const response = await getKitchenItemsScreen();
      // العائد: { data: [[order, ...], ...] }
      let newData = {};
      if (Array.isArray(response?.data)) {
        // دمج جميع الطلبات في مصفوفة واحدة
        const allOrders = response.data.flat();
        allOrders.forEach((order) => {
          if (order && order.id) {
            // أضف order reference لكل صنف لسهولة العرض لاحقًا
            const items = (order.order_items || [])
              .filter((item) => !isExcludedKitchenCategoryItem(item))
              .map((item) => ({ ...item, order }));

            if (items.length > 0) {
              newData[order.id] = items;
            }
          }
        });
      }
      detectKitchenAlerts(newData);
      setData(newData);
    } catch (err) {
      setError(err.message || 'حدث خطأ في جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKitchenData();
    const interval = setInterval(fetchKitchenData, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sort and filter orders
  const orderEntries = Object.entries(data || {})
    .filter(([, items]) => {
      const firstItem = items?.[0] || {};
      const order = firstItem?.order || {};
      return order?.status !== 'under_review' && order?.status !== 'finished';
    })
    .sort(([, itemsA], [, itemsB]) => {
      const activeStatuses = ['pending', 'in_prepration'];
      const priorityA = itemsA.some((i) => activeStatuses.includes(i.status)) ? 0 : 1;
      const priorityB = itemsB.some((i) => activeStatuses.includes(i.status)) ? 0 : 1;
      if (priorityA !== priorityB) return priorityA - priorityB;
      const metaA = getOrderSortMeta(itemsA);
      const metaB = getOrderSortMeta(itemsB);
      if (metaB.openTimeRank !== metaA.openTimeRank) {
        return metaB.openTimeRank - metaA.openTimeRank;
      }
      return metaB.createdAtRank - metaA.createdAtRank;
    });

  function getOrderSortMeta(items = []) {
    const firstItem = items?.[0] || {};
    const order = firstItem?.order || {};
    return {
      openTimeRank: toOpenTimeSeconds(order?.open_time || firstItem?.open_time),
      createdAtRank: toDateMs(order?.created_at || firstItem?.created_at),
    };
  }

  const readyPickupEntries = orderEntries.filter(([, items]) => items.length > 0 && items.every((i) => i.status === 'pickup'));
  const kitchenEntries = orderEntries.filter(([, items]) => items.some((i) => i.status === 'pending' || i.status === 'in_prepration'));
  const visibleEntries = activeTab === 'ready_pickup' ? readyPickupEntries : kitchenEntries;


  // Handle status change dialog and update order status if needed
  const handleOpenChangeStatusDialog = async (orderId, item, orderData) => {
    setApiErrors([]);
    const actionConfig = getActionConfigByStatus(item?.status);

    // إذا كان أول صنف في الطلب وحالته pending، غيّر حالة الطلب إلى in_prepration
    if (actionConfig.nextStatus === 'in_prepration' && orderData && orderData.status === 'pending') {
      // جلب كل الأصناف لهذا الطلب
      const orderItems = (data[orderId] || []);
      // التحقق إذا كان هذا هو أول صنف في الطلب
      const isFirstItem = orderItems.length > 0 && orderItems[0].id === item.id;
      const realOrderId = orderData?.id;
      if (!realOrderId) {
        setApiErrors(['رقم الطلب غير متوفر!']);
        return;
      }
      try {
        // إذا كان أول صنف، غيّر حالة الطلب إلى in_prepration
        if (isFirstItem) {
          await fetchKitchenData();
        }
      } catch (err) {
        if (err.response && err.response.status === 422) {
          const errors = err.response.data.errors;
          setApiErrors(Object.values(errors).flat());
        } else if (err.response && err.response.data && err.response.data.message) {
          setApiErrors([err.response.data.message]);
        } else {
          setApiErrors(['حدث خطأ غير متوقع']);
        }
        return;
      }
    }
    setSelectedOrderId(orderId);
    setSelectedOrderData(orderData || null);
    setSelectedItems(item ? [item] : []);
    setTargetStatus(actionConfig.nextStatus);
    setChangeStatusOpen(true);
  };

  const handleCloseChangeStatusDialog = () => {
    setChangeStatusOpen(false);
    setSelectedOrderId(null);
    setSelectedOrderData(null);
    setSelectedItems([]);
    setTargetStatus('pickup');
  };

  const handleLogout = () => {
    logout();
    navigate('/background/gate/login', { replace: true });
  };

  if (loading && Object.keys(data).length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: '100vh',
        background: `radial-gradient(circle at 10% 8%, ${alpha(theme.palette.primary.main, 0.07)}, transparent 30%), radial-gradient(circle at 90% 85%, ${alpha(theme.palette.warning.main, 0.07)}, transparent 28%), linear-gradient(180deg, #f7faf8 0%, #eef3f0 100%)`,
        p: { xs: 1.5, md: 2.5 },
      }}
    >
      <Snackbar
        open={kitchenAlert.open}
        autoHideDuration={7000}
        onClose={() => setKitchenAlert((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setKitchenAlert((prev) => ({ ...prev, open: false }))}
          sx={{
            width: '100%',
            fontWeight: 900,
            alignItems: 'center',
            boxShadow: `0 12px 30px ${alpha(theme.palette.common.black, 0.18)}`,
          }}
        >
          {kitchenAlert.message}
        </Alert>
      </Snackbar>

      {/* API Errors */}
      {apiErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiErrors.map((msg, idx) => (
            <div key={idx}>{msg}</div>
          ))}
        </Alert>
      )}

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.07)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.warning.main})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <LocalDiningIcon sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                شاشة المطبخ
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75} mt={0.5}>
                <Chip label={`${orderEntries.length} طلب نشط`} size="small" color="primary" />
                <Chip label={`${orderEntries.reduce((s, [, items]) => s + items.length, 0)} صنف`} size="small" color="warning" variant="outlined" />
                <Chip
                  label={`${orderEntries.filter(([, items]) => items.some((i) => i.status === 'pending')).length} جديد`}
                  size="small"
                  color="error"
                  variant="outlined"
                />
                <Chip
                  label={`${orderEntries.filter(([, items]) => items.length > 0 && items.every((i) => i.status === 'pickup')).length} منتهي`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchKitchenData}
              disabled={loading}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              {isMobile ? '' : 'تحديث'}
              {loading && <CircularProgress size={14} sx={{ ml: 0.75 }} color="inherit" />}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<ExitToAppIcon />}
              onClick={handleLogout}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              {isMobile ? '' : 'خروج'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: 2.5,
          border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{ px: 1 }}
        >
          <Tab
            value="kitchen"
            label={`المطبخ (${kitchenEntries.length})`}
            sx={{ fontWeight: 800 }}
          />
          <Tab
            value="ready_pickup"
            label={`جاهز للاستلام (${readyPickupEntries.length})`}
            sx={{ fontWeight: 800 }}
          />
        </Tabs>
      </Paper>

      {/* Orders List */}
      {visibleEntries.length > 0 ? (
        <Grid container spacing={{ xs: 1.25, md: 1.75 }}>
          {visibleEntries.map(([orderId, items]) => {
            const firstItem = items?.[0];
            const order = firstItem?.order || {};
            const orderType = order.order_type || '-';
            const openedAt = order.open_time || firstItem?.open_time;
            const createdAt = order.created_at || firstItem?.created_at;
            const orderStatus = order.status || '-';
            const generalInc = order.general_increment ?? orderId;
            const dailyInc = order.daily_increment ?? '-';
            const tableId = order.table_id ?? null;
            const tableName = order.table?.name ?? null;
            const tableNumber = order.table?.number ?? null;
            const tableLabel = tableName || (tableNumber ? `#${tableNumber}` : null) || (tableId ? `#${tableId}` : null);
            const allPickup = items.length > 0 && items.every((i) => i.status === 'pickup');

            return (
              <Grid item xs={12} md={6} xl={4} key={orderId}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: `2px solid ${alpha(allPickup ? theme.palette.success.main : theme.palette.primary.main, allPickup ? 0.55 : 0.2)}`,
                    boxShadow: allPickup
                      ? `0 6px 20px ${alpha(theme.palette.success.main, 0.16)}`
                      : `0 6px 20px ${alpha(theme.palette.common.black, 0.06)}`,
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Order Header */}
                  <Box
                    sx={{
                      p: { xs: 1.1, sm: 1.35 },
                      background: allPickup
                        ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.18)} 0%, ${alpha(theme.palette.success.main, 0.06)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                      borderBottom: `1px solid ${alpha(allPickup ? theme.palette.success.main : theme.palette.primary.main, 0.15)}`,
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={0.75}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Badge badgeContent={items.length} color="primary">
                          <Box
                            sx={{
                              width: 34, height: 34, borderRadius: 1.75,
                              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <LocalDiningIcon sx={{ color: '#fff', fontSize: 18 }} />
                          </Box>
                        </Badge>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
                            طلب #{generalInc}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            رقم يومي: #{dailyInc}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" flexWrap="wrap" gap={0.6}>
                        <Chip
                          label={orderTypeLabel(orderType)}
                          size="small"
                          color={getOrderTypeColor(orderType)}
                          sx={{ fontWeight: 700 }}
                        />
                        {orderType === 'tables' && tableLabel && (
                          <Chip
                            label={`طاولة ${tableLabel}`}
                            size="small"
                            color="success"
                            sx={{ fontWeight: 800 }}
                          />
                        )}
                        <Chip
                          label={orderStatusLabel(orderStatus)}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 700 }}
                        />
                      </Stack>
                    </Stack>

                    {/* Order meta row */}
                    <Stack direction="row" flexWrap="wrap" gap={0.6} mt={0.8}>
                      <Paper
                        variant="outlined"
                        sx={{
                          px: 0.8,
                          py: 0.45,
                          borderRadius: 1.75,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.6,
                          borderColor: alpha(theme.palette.warning.main, 0.45),
                          bgcolor: alpha(theme.palette.warning.main, 0.12),
                        }}
                      >
                        <AccessTimeIcon sx={{ fontSize: 16, color: 'warning.dark' }} />
                        <Typography variant="caption" sx={{ fontWeight: 900, color: 'warning.dark' }}>
                          وقت الفتح: {formatTime(openedAt)}
                        </Typography>
                      </Paper>
                      {createdAt && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`الإنشاء: ${new Date(createdAt).toLocaleString('ar-EG', { hour12: true })}`}
                          sx={{ fontWeight: 700 }}
                        />
                      )}
                    </Stack>

                    {/* Ready for pickup banner */}
                    {allPickup && (
                      <Paper
                        variant="outlined"
                        sx={{
                          mt: 0.8,
                          px: 1,
                          py: 0.6,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          borderColor: alpha(theme.palette.success.main, 0.6),
                          bgcolor: alpha(theme.palette.success.main, 0.13),
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} />
                        <Typography variant="body2" sx={{ fontWeight: 900, color: 'success.dark' }}>
                          جاهز للاستلام
                        </Typography>
                      </Paper>
                    )}
                  </Box>

                  {/* Items */}
                  <Box sx={{ p: { xs: 0.9, sm: 1.1 }, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(items || []).map((item) => {
                      const isDrink = !!item.drinks;
                      const drink = item.drinks || {};
                      const product = item.product || {};
                      const itemTypeLabel = isDrink ? 'مشروب مخصص' : 'منتج';
                      const itemName = isDrink
                        ? (drink.drink_name || drink.name || 'مشروب')
                        : (product.name || 'منتج');
                      const materials = isDrink && Array.isArray(drink.materials) ? drink.materials : [];
                      const actionConfig = getActionConfigByStatus(item.status);

                      return (
                        <Paper
                          key={item.id}
                          variant="outlined"
                          sx={{
                            p: { xs: 0.95, sm: 1.1 },
                            borderRadius: 2,
                            borderWidth: isDrink ? 2 : 1,
                            borderColor: isDrink
                              ? alpha(theme.palette.secondary.main, 0.3)
                              : alpha(theme.palette.primary.main, 0.25),
                            bgcolor: isDrink
                              ? alpha(theme.palette.secondary.main, 0.03)
                              : alpha(theme.palette.primary.main, 0.02),
                          }}
                        >
                          {/* Item header */}
                          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.75} mb={0.65}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              {isDrink
                                ? <LocalCafeIcon sx={{ fontSize: 20, color: 'secondary.main', flexShrink: 0 }} />
                                : <RestaurantMenuIcon sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
                              }
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                  {itemName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    fontWeight: 800,
                                    color: isDrink ? 'secondary.main' : 'primary.main',
                                  }}
                                >
                                  {itemTypeLabel}
                                </Typography>

                              </Box>
                            </Stack>
                            <Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                              <Chip
                                size="small"
                                color={isDrink ? 'secondary' : 'primary'}
                                label={itemTypeLabel}
                                sx={{ fontWeight: 800 }}
                              />
                              {getStatusChip(item.status)}
                            </Stack>
                          </Stack>

                          {/* Quantity + Price + Total */}
                          <Stack direction="row" flexWrap="wrap" gap={0.55} mb={0.75}>
                            <Chip
                              label={`الكمية: ${Number(item.quantity || 0)}`}
                              size="small" variant="outlined"
                              sx={{ fontWeight: 700 }}
                            />
                            {!isDrink && Number(product.prepare_time) > 0 && (
                              <Chip
                                icon={<AccessTimeIcon />}
                                label={`وقت التحضير: ${product.prepare_time} د`}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 700 }}
                              />
                            )}
                          </Stack>

                          {/* Drink-specific details */}
                          {isDrink && (
                            <Box mb={1}>
                              <Stack direction="row" flexWrap="wrap" gap={0.6} mb={0.75}>
                                {drink.temperature && (
                                  <Chip
                                    icon={<ThermostatIcon />}
                                    label={drink.temperature}
                                    size="small" color="error" variant="outlined"
                                    sx={{ fontWeight: 700 }}
                                  />
                                )}
                                {drink.taste && (
                                  <Chip
                                    label={`الطعم: ${drink.taste}`}
                                    size="small" color="warning" variant="outlined"
                                    sx={{ fontWeight: 700 }}
                                  />
                                )}
                              </Stack>
                              {drink.description && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    mb: 0.75,
                                    px: 1,
                                    py: 0.65,
                                    borderRadius: 1.5,
                                    color: 'text.secondary',
                                    bgcolor: alpha(theme.palette.background.paper, 0.78),
                                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.14)}`,
                                    lineHeight: 1.7,
                                  }}
                                >
                                  {drink.description}
                                </Typography>
                              )}
                              {/* Materials */}
                              {materials.length > 0 && (
                                <Box
                                  sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                                    bgcolor: alpha(theme.palette.secondary.main, 0.04),
                                    p: 1,
                                  }}
                                >
                                  <Stack direction="row" alignItems="center" spacing={0.5} mb={0.75}>
                                    <ScienceIcon sx={{ fontSize: 15, color: 'secondary.main' }} />
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'secondary.main' }}>
                                      المكونات ({materials.length})
                                    </Typography>
                                  </Stack>
                                  <Stack spacing={0.4}>
                                    {materials.map((mat) => {
                                      const matName = mat?.material?.name || mat?.material?.name_en || `#${mat?.material_id}`;
                                      const matQty = mat?.quantity ?? '-';
                                      const matUnit = mat?.material?.measurment_unit?.abbreviation || mat?.material?.measurment_unit?.name || '';
                                      return (
                                        <Stack
                                          key={mat.id}
                                          direction="row"
                                          justifyContent="space-between"
                                          alignItems="center"
                                          sx={{
                                            px: 1, py: 0.35,
                                            borderRadius: 1.5,
                                            bgcolor: alpha(theme.palette.background.paper, 0.85),
                                            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                            {matName}
                                          </Typography>
                                          <Chip
                                            label={`${Number(matQty)} ${matUnit}`}
                                            size="small"
                                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 800 }}
                                          />
                                        </Stack>
                                      );
                                    })}
                                  </Stack>
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* Note */}
                          {item.note && (
                            <Box
                              sx={{
                                mb: 1, p: 0.75,
                                borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.warning.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                                ملاحظة: {item.note}
                              </Typography>
                            </Box>
                          )}

                          <Divider sx={{ mb: 1 }} />

                          {/* Action button */}
                          {/* Action button: only allow valid status transitions */}
                          <Button
                            size="small"
                            variant="contained"
                            color={item.status === 'pending' ? 'error' : 'success'}
                            fullWidth
                            onClick={() => handleOpenChangeStatusDialog(order.id, item, order)}
                            disabled={!['pending', 'in_prepration', 'out_of_preparation'].includes(item.status)}
                            sx={{ borderRadius: 2, fontWeight: 800 }}
                          >
                            {actionConfig.label}
                          </Button>
                        </Paper>
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 5, textAlign: 'center', borderRadius: 3,
            border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
          }}
        >
          <LocalDiningIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {activeTab === 'ready_pickup' ? 'لا توجد طلبات جاهزة للاستلام حالياً' : 'لا توجد طلبات مطبخ حالياً'}
          </Typography>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchKitchenData} sx={{ mt: 2, borderRadius: 2 }}>
            تحديث
          </Button>
        </Paper>
      )}

      {/* Dialog for changing item status */}
      <ChangeOrderItemsStatue
        open={changeStatusOpen}
        onClose={handleCloseChangeStatusDialog}
        orderId={selectedOrderId}
        orderData={selectedOrderData}
        items={selectedItems}
        status={targetStatus}
        onSuccess={fetchKitchenData}
      />
    </Box>
  );
};

export default KitchenScreen;
